import React, { useState, useEffect } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import ErrorBoundary from "@/components/common/ErrorBoundary"
// Import the refactored components
import { ProfileHeader } from "./profile/ProfileHeader"
import { OverviewTabContent } from "./profile/OverviewTabContent"
import { PersonalTabContent } from "./profile/PersonalTabContent"
import { OvertimeTabContent } from "./profile/OvertimeTabContent"
import { ProfileActionButtons } from "./profile/ProfileActionButtons"
import { EmployeeStatus, ResidencyType } from '@/contexts/employees/EmployeesContext';

interface EmployeeProfileProps {
  employee: {
    id: string
    name: string
    email: string
    role: string
    department: string[]
    shift: string
    position: string
    phone?: string
    hireDate?: string
    status?: string
    residency?: string
    address?: string
    bio?: string
  }
  onClose: () => void
}

export function EmployeeProfile({ employee, onClose }: EmployeeProfileProps) {
  const [activeTab, setActiveTab] = useState("overview");
  
  // Remove detailed logging for component lifecycle and error tracking
  useEffect(() => {
    // Employee profile mounted
  }, [employee]);

  // Safely handle tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // Handle close action properly - simplified to avoid timing issues
  const handleClose = () => {
    setActiveTab("overview");
    onClose();
  };

  // Safety check in case employee is null
  if (!employee) {
    return <div className="p-4">Employee data not available</div>;
  }

  // Prepare the correct prop shapes for tab content
  const overviewEmployee = {
    id: employee.id,
    email: employee.email,
    created_at: employee.hireDate || ""
  };
  
  const nameParts = (employee.name || '').split(' ');
  const normalizedEmployee = {
    ...employee,
    first_name: nameParts[0] || '',
    last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
    status: employee.status || EmployeeStatus.ACTIVE,
    residency: employee.residency || ResidencyType.RESIDENT,
    worker_type: 'worker_type' in employee && typeof (employee as Record<string, unknown>).worker_type === 'string'
      ? (employee as Record<string, string>).worker_type
      : '',
    department: Array.isArray(employee.department) ? employee.department : [employee.department || ''],
    // Add missing required fields for ProfileHeader
    created_at: employee.hireDate || new Date().toISOString(),
    updated_at: new Date().toISOString(),
    company_id: '', // This will be set from context if needed
    employee_id: employee.id || '',
  };

  // Create employee object for PersonalTabContent with correct types
  const personalTabEmployee = {
    ...employee,
    first_name: nameParts[0] || '',
    last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
    status: employee.status || EmployeeStatus.ACTIVE,
    residency: employee.residency || ResidencyType.RESIDENT,
    worker_type: 'worker_type' in employee && typeof (employee as Record<string, unknown>).worker_type === 'string'
      ? (employee as Record<string, string>).worker_type
      : '',
    department: Array.isArray(employee.department) ? employee.department.join(', ') : (employee.department || ''),
    created_at: employee.hireDate || new Date().toISOString(),
    updated_at: new Date().toISOString(),
    company_id: '',
    employee_id: employee.id || '',
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <ProfileHeader employee={normalizedEmployee} />

        <Tabs defaultValue="overview" value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="personal">Personal Details</TabsTrigger>
            <TabsTrigger value="overtime">Overtime History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-6">
            <OverviewTabContent employee={overviewEmployee} />
          </TabsContent>
          
          <TabsContent value="personal">
            <PersonalTabContent employee={personalTabEmployee} />
          </TabsContent>
          
          <TabsContent value="overtime">
            <OvertimeTabContent employeeId={employee.id} />
          </TabsContent>
        </Tabs>
        
        <ProfileActionButtons onClose={handleClose} />
      </div>
    </ErrorBoundary>
  );
}
